import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Clock, Building2, User, CheckCircle2 } from "lucide-react";
import { categoryLabel } from "@/lib/constants";
import { toast } from "sonner";

export default function CalendarPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [range, setRange] = useState("week");
  const [rsvped, setRsvped] = useState({});

  useEffect(() => { api.get("/calendar").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  const doRsvp = async (id) => {
    try { await api.post(`/lectures/${id}/rsvp`); setRsvped((s) => ({ ...s, [id]: true })); toast.success("تم تأكيد نيتك للحضور"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    if (!items) return [];
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    return items.filter((l) => {
      if (!l.date) return false;
      if (range === "all") return true;
      const d = new Date(l.date);
      const diff = (d - new Date(iso(today))) / (1000 * 60 * 60 * 24);
      if (range === "day") return l.date === iso(today);
      if (range === "week") return diff >= 0 && diff < 7;
      if (range === "month") return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
      return true;
    }).sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
  }, [items, range]);

  if (items === null) return <PageLoader />;

  const grouped = filtered.reduce((acc, l) => { (acc[l.date] = acc[l.date] || []).push(l); return acc; }, {});

  return (
    <>
      <PageHeader title="التقويم العلمي" subtitle="جميع المحاضرات والأنشطة العلمية"
        action={
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger data-testid="calendar-range" className="w-40 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="day">اليوم</SelectItem>
              <SelectItem value="week">هذا الأسبوع</SelectItem>
              <SelectItem value="month">هذا الشهر</SelectItem>
              <SelectItem value="all">الكل</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      {filtered.length === 0 ? <EmptyState icon={CalendarDays} title="لا توجد أنشطة في هذه الفترة" /> : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, lecs]) => (
            <div key={date}>
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy text-white"><CalendarDays className="h-4 w-4" /></div>
                <p className="font-heading font-bold text-navy">{date}</p>
                <span className="text-xs text-slate-400">({lecs.length} محاضرة)</span>
              </div>
              <div className="stagger space-y-3 border-r-2 border-slate-100 pe-2 ps-5">
                {lecs.map((l) => (
                  <div key={l.id} data-testid={`cal-${l.id}`} className="rounded-xl border border-slate-100 bg-white p-4 soft-shadow card-hover">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="font-semibold text-navy">{l.title}</h4>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-greenx">{l.program_name}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {l.start_time} - {l.end_time}</span>
                      <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {l.lecturer_name || "—"}</span>
                      <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {l.unit_name || "—"}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5">{categoryLabel[l.gender]}</span>
                    </div>
                    {user.role === "student" && l.date >= today && (
                      <div className="mt-3">
                        {(l.rsvped || rsvped[l.id]) ? (
                          <span data-testid={`rsvp-done-${l.id}`} className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> أكّدت حضورك</span>
                        ) : l.attended ? (
                          <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> تم تسجيل حضورك</span>
                        ) : (
                          <button onClick={() => doRsvp(l.id)} data-testid={`rsvp-${l.id}`} className="rounded-lg bg-gold px-4 py-1.5 text-xs font-bold text-navy transition-transform hover:scale-105 active:scale-95">موافق، سأحضر</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
